import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const ConfigureContext = createContext();

export const ConfigureProvider = ({ children, connectionString }) => {
    const { selectedCompany } = useAuth();

    const [configureData, setConfigureData] = useState([]);
    const [configureLoading, setConfigureLoading] = useState(false);

    const requestIdRef = useRef(0);
    const hasLoadedRef = useRef(false);
    const lastCompanyRef = useRef(null);

    const fetchConfigureData = useCallback(async (companyId) => {
        if (!companyId) return;
        const requestId = ++requestIdRef.current;
        // Only the first load shows a loading state; refreshes keep the current flags on screen.
        if (!hasLoadedRef.current) setConfigureLoading(true);
        try {
            const res = await axios.get(`${connectionString}/api/configure/getDetails/${companyId}`);
            // A newer request or a company switch superseded this response: ignore it.
            if (requestId !== requestIdRef.current) return;
            setConfigureData(Array.isArray(res.data) ? res.data : []);
            hasLoadedRef.current = true;
        } catch (err) {
            console.error('configure fetch error:', err);
            // Keep the last good flags. Clearing them would silently disable every feature.
        } finally {
            if (requestId === requestIdRef.current) setConfigureLoading(false);
        }
    }, [connectionString]);

    // On a company switch, drop the previous company's flags and invalidate any in-flight response.
    useEffect(() => {
        const id = selectedCompany?.CompId || null;
        if (lastCompanyRef.current !== null && lastCompanyRef.current !== id) {
            requestIdRef.current += 1;
            hasLoadedRef.current = false;
            setConfigureData([]);
        }
        lastCompanyRef.current = id;
    }, [selectedCompany?.CompId]);

    // AuthContext dispatches this ~2.8s after user + selectedCompany are ready
    // (see the 'configureTrigger' useEffect in AuthContext.js)
    useEffect(() => {
        const handler = (e) => {
            const companyId = e.detail?.companyId;
            if (companyId) fetchConfigureData(companyId);
        };
        window.addEventListener('configureTrigger', handler);
        return () => window.removeEventListener('configureTrigger', handler);
    }, [fetchConfigureData]);

    useEffect(() => {
        console.log('configureData:', configureData);
    }, [configureData]);

    // Get all configure records
    const getAllConfigure = useCallback(() => {
        return configureData || [];
    }, [configureData]);

    // Get only enabled configurations
    const getEnabledConfigure = useCallback(() => {
        return configureData.filter(r => Boolean(r.ApplyConfigure));
    }, [configureData]);

    // Get configure record by ConfigAutoId
    const getConfigureById = useCallback((configAutoId) => {
        return configureData.find(r => Number(r.ConfigAutoId) === configAutoId);
    }, [configureData]);

    // Get configure records by ConfigTypeId
    const getConfigureByType = useCallback((configTypeId) => {
        return configureData.filter(r => Number(r.ConfigTypeId) === configTypeId);
    }, [configureData]);

    // Get configure record by DisplayMessage (label)
    const getConfigureByLabel = useCallback((displayMessage) => {
        return configureData.find(r => r.DisplayMessage === displayMessage);
    }, [configureData]);

    // Check if a specific feature is enabled by DisplayMessage
    const isFeatureEnabled = useCallback((displayMessage) => {
        const config = getConfigureByLabel(displayMessage);
        return config ? Boolean(config.ApplyConfigure) : false;
    }, [getConfigureByLabel]);

    // Memoize refetch callback separately to maintain stable reference
    const refetchConfigureData = useCallback(() => {
        fetchConfigureData(selectedCompany?.CompId);
    }, [fetchConfigureData, selectedCompany?.CompId]);

    // Memoize context value to prevent unnecessary re-renders of entire app tree
    const contextValue = useMemo(() => ({
        configureData,
        configureLoading,
        refetchConfigureData,
        getAllConfigure,
        getEnabledConfigure,
        getConfigureById,
        getConfigureByType,
        getConfigureByLabel,
        isFeatureEnabled,
    }), [
        configureData,
        configureLoading,
        refetchConfigureData,
        getAllConfigure,
        getEnabledConfigure,
        getConfigureById,
        getConfigureByType,
        getConfigureByLabel,
        isFeatureEnabled,
    ]);

    return (
        <ConfigureContext.Provider value={contextValue}>
            {children}
        </ConfigureContext.Provider>
    );
};

export const useConfigure = () => useContext(ConfigureContext);
